import * as React from 'react';
import {useState} from 'react';

import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import CartManager, {
    getDiscountedItemPrice,
    getItemPrice,
    getTotalPriceOfCart,
    useCartChange
} from "../../managers/cartManager/cartManager";
import {Trans, useTranslation} from "react-i18next";
import Counter from "../../components/counter/Counter";
import {grey} from "@mui/material/colors";
import Grid from "@mui/material/Grid2";
import CartItemParams from "./CartItemParams";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import IconButton from "@mui/material/IconButton";
import {FormHelperText, InputAdornment} from "@mui/material";
import {ClearOutlined} from "@mui/icons-material";
import {useNavigate} from "react-router";
import ListItem from "@mui/material/ListItem";

function Info({goBackToFirstStep}) {
    const cart = useCartChange();
    const {t} = useTranslation();
    const [promoCode, setPromoCode] = useState('');
    const totalPrice = getTotalPriceOfCart(cart);
    const navigate = useNavigate();
    const shippingPrice = CartManager.getShippingPrice();
    const shipping = CartManager.getShipping();

    const showShippingPrice = shipping && Object.keys(cart?.items || {}).length && shippingPrice;

    const submitPromoCode = () => {
        CartManager.addPromoCode(promoCode);
    }

    const removePromoCode = () => {
        CartManager.addPromoCode('');
        setPromoCode('');
    }

    const goToPage = (id) => {
        navigate(`/products/${id}`);
    }

    const getShippingPrice = () => {
        return showShippingPrice ? shippingPrice : 0;
    }

    return (cart?.items &&
        <React.Fragment>
            <Typography variant="subtitle2" sx={{color: 'text.secondary', textAlign: 'right'}}>
                <Trans i18nKey={'cart.total'}/>
            </Typography>
            {cart?.discount ? <ListItemText sx={{maxHeight: '80px'}}
                    primary={<Typography variant="h4" gutterBottom sx={{textAlign: 'right'}}>
                        {(totalPrice * (1 - cart.discount) + getShippingPrice()).toFixed(2)} Lei
                    </Typography>}
                    secondary={<Typography variant="body1" sx={{
                        fontWeight: 'medium',
                        textDecoration: 'line-through',
                        color: grey[400]
                    }} textAlign={'right'}>
                        {(totalPrice + getShippingPrice()).toFixed(2)} Lei
                    </Typography>}
                /> :
                <Typography variant="h4" gutterBottom sx={{textAlign: 'right'}}>
                    {(totalPrice + getShippingPrice()).toFixed(2)} Lei
                </Typography>}
            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                <Box sx={{width: '200px', display: 'flex', flexDirection: 'column',
                    right: (cart.promoCode ? '-13px' : '-28px'),
                    top: '-15px'
                }}>
                    {cart.promoCode ?
                        <Typography variant="body2" sx={{color: 'text.secondary', textAlign: 'right'}}>
                            {`${cart.promoCode} (-${(cart.discount || 0) * 100}%)`}
                            <IconButton>
                                <ClearOutlined fontSize="small" onClick={removePromoCode}/>
                            </IconButton>
                        </Typography> :
                        <>
                            <TextField
                                sx={{transform: 'scale(0.7)'}}
                                variant="standard"
                                id="promoCode"
                                name="promoCode"
                                type="text"
                                placeholder={t('cart.promoCode')}
                                required
                                size="small"
                                value={promoCode}
                                onKeyDown={(event) => (event.keyCode === 13 && submitPromoCode())}
                                onChange={(event) => setPromoCode(event.target.value)}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={submitPromoCode}
                                                    edge="end"
                                                >
                                                    <KeyboardReturnIcon/>
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            /></>}
                </Box>
            </Box>
            <List disablePadding>
                {showShippingPrice ? <ListItem sx={{py: 1, px: 0}}>
                    <ListItemText primary={t('cart.shipping')} secondary={t(`shippingForm.${shipping.type}`)}/>
                    <Typography variant="body1" sx={{fontWeight: 'medium'}}
                                               textAlign={'right'}>{shippingPrice.toFixed(2)} Lei</Typography>
                </ListItem> : ''}
                {Object.keys(cart?.items).map((key) => {
                    const item = cart?.items[key] || {};
                    const nr = item.nr || 1;
                    const productId = item.productId || 1;
                    const paramValues = item.params || {};
                    const discount = item.discount || 0;
                    const product = item.product || {};
                    const minPiece = product.minPiece || 1;

                    return (
                        <Grid container key={key} sx={{py: 1, px: 0}} flexDirection={'row'}>
                            <Grid
                                size={8}
                                sx={{flexDirection: 'column', alignItems: 'flex-start', textDecoration: (product.disabled ? 'line-through': '')}}
                            >
                                <ListItemText
                                    sx={{cursor: 'pointer'}}
                                    onClick={() => goToPage(productId)}
                                    primary={`${t(product.name)} (${getItemPrice(item).toFixed(2)} Lei${discount ? ` -${(discount * 100).toLocaleString()}%` : ''})`}
                                />
                                <CartItemParams params={product.params} values={paramValues} itemKey={key}
                                                productId={productId}/>
                                <Counter
                                    count={nr}
                                    disabled={product.disabled}
                                    setCount={(value) => {
                                        if (!CartManager.validateSelectedDesigns()) {
                                            goBackToFirstStep();
                                        }

                                        if (value < minPiece) {
                                            CartManager.removeItemsFromCart(key);
                                            if (CartManager.getTotalNumberOfItems() === 0) {
                                                goBackToFirstStep();
                                            }
                                        } else {
                                            CartManager.changeCartItemNr(key, value);
                                        }
                                    }}
                                    minValue={minPiece}
                                />
                            </Grid>

                            <Grid
                                sx={{textDecoration: (product.disabled ? 'line-through': '')}}
                                size={4}
                            >{discount ? <ListItemText
                                    primary={<Typography variant="body1" sx={{fontWeight: 'medium'}} textAlign={'right'}>
                                        {(nr * getDiscountedItemPrice(item)).toFixed(2)} Lei
                                    </Typography>}
                                    secondary={<Typography variant="body1" sx={{
                                        fontWeight: 'medium',
                                        textDecoration: 'line-through',
                                        color: grey[400]
                                    }} textAlign={'right'}>
                                        {(getItemPrice(item) * nr).toFixed(2)} Lei
                                    </Typography>}
                                /> :
                                <ListItemText
                                    primary={<Typography variant="body1" sx={{fontWeight: 'medium'}}
                                                         textAlign={'right'}>
                                        {(getItemPrice(item) * nr).toFixed(2)} Lei
                                    </Typography>}
                                    secondary={<Typography variant="body1" sx={{
                                        fontWeight: 'medium',
                                        textDecoration: 'line-through',
                                        color: grey[200]
                                    }}
                                                           textAlign={'right'}>&nbsp;
                                    </Typography>}
                                />
                            }</Grid>
                            {product.disabled ? <FormHelperText sx={{textDecoration: 'none'}} error>
                                <Trans i18nKey={'cart.disabledItem'}/>
                            </FormHelperText> : ''}
                        </Grid>
                    )
                })}
            </List>
        </React.Fragment>
    );
}

export default Info;
